import React, { useState } from "react";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import SaveIcon from "@material-ui/icons/Save";
import { ConfirmDialog } from "src/apps/commons/dataDisplays";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import { listAllRemyndBrands } from "src/apps/commons/dropdowns/api";
import { IRemyndBrandBrief } from "src/apps/modules/remyndBrand/types";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import * as Api from "../api";
import { usePreviewData } from "../hooks";
import {
  IRemyndScrapeCategoryMappingPreviewReq,
  IRemyndScrapeCategoryMappingUpdateReq
} from "../types";
import PreviewUpdateForm from "./PreviewUpdateForm";

const useStyles = makeStyles(_ => ({
  layoutButton: {
    flex: "1 1 auto"
  }
}));

// ---------------------------------------------------------------------------

const PreviewContainers: React.FC<unknown> = _ => {
  const classes = useStyles();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [filterBrand, setFilterBrand] =
    useState<IDropdown<IRemyndBrandBrief> | null>(null);
  const [filterReq, setFilterReq] =
    useState<IRemyndScrapeCategoryMappingPreviewReq | undefined>();

  const {
    state: { data, fetching },
    fetchFullData: fetchData
  } = usePreviewData(filterReq);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmDialogState, setConfirmDialogState] = useState<{
    open: boolean;
    message?: string;
    onConfirm?: () => void;
  }>({ open: false });

  const handleSubmit = () => {
    if (!data || !filterReq || fetching || saving) return;

    const mappableItems = data.items.filter(it => Boolean(it.matchedRule));
    const affectedProductCount = mappableItems.reduce(
      (sum, it) => sum + it.category.productCount,
      0
    );
    const req: IRemyndScrapeCategoryMappingUpdateReq = {
      filter: filterReq.filter,
      items: mappableItems.map(it => ({
        brandId: it.category.brandId,
        categoryTextTh: it.category.categoryTextTh,
        categoryTextEn: it.category.categoryTextEn,
        categoryLv1Id: it.matchedRule?.categoryLv1Id,
        categoryLv2Id: it.matchedRule?.categoryLv2Id,
        categoryLv3Id: it.matchedRule?.categoryLv3Id
      }))
    };

    setConfirmDialogState({
      open: true,
      message: `อาจจะกระทบกับสินค้าจำนวน ${affectedProductCount} รายการ`,
      onConfirm: async () => {
        try {
          setSaving(true);
          const updatedCount = (await Api.processUpdate(req)).value;
          await fetchData();
          setMessage(
            "success",
            `บันทึกข้อมูล หมวดหมู่สินค้า สำเร็จ (จำนวน record ที่ update จริง: ${updatedCount} รายการ)`
          );
          setSaved(true);
        } catch (e) {
          handleError(e);
        } finally {
          setSaving(false);
          handleCloseDialog();
        }
      }
    });
  };

  const handleCloseDialog = () => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <AsyncDropdown
            label={"กรองเฉพาะแบรนด์ (optional)"}
            fetchDropdown={listAllRemyndBrands}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME_TH",
                desc: false
              }
            }}
            value={filterBrand}
            onChange={setFilterBrand}
            disabled={fetching || saving}
          />
        </Grid>

        <Grid item className={classes.layoutButton} />
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="button"
            startIcon={
              fetching ? (
                <CircularProgress color="secondary" size={20} />
              ) : undefined
            }
            disabled={fetching || saving}
            onClick={() => {
              setSaved(false);
              setFilterReq({
                filter: {
                  brandId: filterBrand?.value,
                  excludePartnerProducts: false // Todo
                }
              });
            }}
          >
            Load Preview
          </Button>
        </Grid>

        {filterReq && data && (
          <>
            <PreviewUpdateForm data={data} />

            <Grid item className={classes.layoutButton} />
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="button"
                startIcon={
                  fetching ? (
                    <CircularProgress color="secondary" size={20} />
                  ) : saved ? (
                    <CheckIcon />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={fetching || saving || saved}
                onClick={handleSubmit}
              >
                Update Products
              </Button>
            </Grid>

            <ConfirmDialog
              title="ยืนยันการบันทึกข้อมูลหมวดหมู่สินค้า"
              description={confirmDialogState.message}
              open={confirmDialogState.open}
              onClose={handleCloseDialog}
              onConfirm={confirmDialogState.onConfirm}
              confirming={saving}
            />
          </>
        )}
      </Grid>
    </>
  );
};

export default PreviewContainers;
